
import { LanguageDefinitionUserGroups } from './languages/definitions/UserGroups';
import { LanguageDefinitionTimesheet } from './languages/definitions/Timesheet';
import {HelperTextLanguageDefinition, LanguageDefinitionHelp} from './languages/definitions/Help';
import { LanguageDefinitionNotifications } from './languages/definitions/Notifications';
import { LanguageDefinitionUserSettings } from './languages/definitions/UserSettings';
import { LanguageDefinitionProject } from './languages/definitions/Project';
import {  LanguageDefinitionError } from './languages/definitions/Error';
import { LanguageDefinitionUserManagement } from './languages/definitions/UserManagement';
import { LanguageDefinitionPrivileges } from './languages/definitions/Privileges';
import {
    LanguageDefinitionEmployeeContract,
    LanguageDefinitionEmployeeDetailView,
    LanguageDefinitionEmployeeView
} from './languages/definitions/Employee';
import {LanguageDefinitionCalendar} from "./languages/definitions/Calendar";
import {DeLocalizedUtils, German} from "./languages/German";
import DateFnsUtils from '@date-io/date-fns';
import deLocale from 'date-fns/locale/de';
import enLocale from 'date-fns/locale/en-GB';
import {
    LanguageDefinitionCommunication,
    LanguageDefinitionCommunicationMethod
} from "./languages/definitions/Communication";
import {LanguageDefinitionQualification} from "./languages/definitions/Qualification";
import {LanguageDefinitionEmployer} from "./languages/definitions/Employer";
import {LanguageDefinitionAjaxResponses} from "./languages/definitions/AjaxResponses";
import { AppState } from '../reducers/RootReducer';
import { StateObservable } from 'redux-observable';
import {LanguageState} from "../reducers/common/GeneralSettingsReducer";
import {LanguageDefinitionTimesheetTask} from "./languages/definitions/TimesheetTask";
import {LanguageDefinitionProjectLog} from "./languages/definitions/ProjectLog";
import {CustomerLanguageDefinition} from "./languages/definitions/Customer";
import {LanguageDefinitionContractor} from "./languages/definitions/Contractor";
import {
    ExternalWorkAreaLanguageDefinition,
    ExternalWorkGroupLanguageDefinition,
    ExternalWorkLanguageDefinition
} from "./languages/definitions/ExternalWork";
import {ProjectCommissionStateLanguageDef} from "./languages/definitions/ProjectCommissionStateLanguageDef";
import {ProjectSegmentFilterLanguageDef} from "./languages/definitions/ProjectSegmentFilterLanguageDef";
import {ProjectRolesLanguageDef} from "./languages/definitions/ProjectRolesLanguageDef";
import {AccountingLanguageDef} from "./languages/definitions/AccountingLanguageDef";
import {CookieBannerLangDef} from "./languages/definitions/CookieBanner";
import {English} from "./languages/English";
import {LanguageDefinitionUserCP} from "./languages/definitions/LanguageDefinitionUserCP";
import {TeamLanguageDef} from "./languages/definitions/TeamLanguageDef";
import {ApiKeysLangDef} from "./languages/definitions/ApiKeysLangDef";


/**
 * Represents a structure to define a language property.
 *
 * This interface contains a single property to specify a language, represented
 * by the `Language` type.
 */
export interface LanguageProp {
    language: Language
}

/**
 * Represents a property that includes a language state, which may be injected
 * or provided externally for use in multi-language or internationalization contexts.
 *
 * @interface LanguageInjectedProp
 * @property {LanguageState} language - The current state of the language, typically
 * used for handling localization or translation-related operations.
 */
export interface LanguageInjectedProp {
    language: LanguageState
}

/**
 * Retrieves the current language from the application's state store.
 *
 * @param {StateObservable<AppState>} store$ - The observable state store containing the application state.
 * @returns {Language} The currently selected language from the application's state.
 */
export const currentLanguageFromStore = ( store$: StateObservable< AppState > ): Language => store$.value.LanguageReducer.current;

/**
 * Represents a button specifically designed for selecting or interacting with language definitions.
 */
export interface LanguageDefinitionButton {
    buttonText: string
}

/**
 * Represents a definition tooltip for a programming language or text content.
 * This interface defines the structure for providing contextual information
 * in the form of a tooltip.
 *
 * @interface LanguageDefinitionTooltip
 * @property {string} tooltipText - The textual content displayed within the tooltip.
 */
export interface LanguageDefinitionTooltip {
    tooltipText: string
}

/**
 * Represents a language definition input placeholder used for localization or UI purposes.
 *
 * This interface defines the structure for placeholder text associated with language-specific input fields or components.
 */
export interface LanguageDefinitionInputPlaceholder {
    placeholderText: string
}

/**
 * Represents a common view for language definition.
 *
 * This interface defines the structure for a language definition's
 * common properties to be displayed in the user interface.
 *
 * @interface LanguageDefinitionCommonView
 *
 * @property {string} title - The title representing the language definition.
 */
export interface LanguageDefinitionCommonView {
    title: string
}

/**
 * An object representing supported languages.
 *
 * Properties:
 *  - german: Specifies the German language.
 *  - english: Specifies the English language.
 */
export const Languages = {
    german: "german",
    english: "english"
};

/**
 * A mapping of locale strings to their corresponding date utility implementations.
 *
 * This object is used to handle locale-specific date utility functions,
 * where each key represents a locale identifier (e.g., "en-GB" for British English,
 * "de-DE" for German in Germany), and the value is the utility class or function
 * designed to manage date-related operations for that locale.
 *
 * Keys:
 * - "en-GB": Represents the English (United Kingdom) locale utilizing `DateFnsUtils` for date management.
 * - "de-DE": Represents the German (Germany) locale utilizing `DeLocalizedUtils` for locale-specific date operations.
 *
 * Values:
 * - Each value is expected to provide methods and functionality tailored for date handling
 *   in the corresponding locale.
 */
export const localeDateUtilsMap: any = {
    "en-GB": DateFnsUtils,
    "de-DE": DeLocalizedUtils
};

/**
 * A mapping of locale identifiers to their corresponding locale data objects.
 *
 * This object is used to associate locale codes with the respective localized
 * data, enabling support for internationalization and region-specific content.
 *
 * Key-value pairs:
 * - The key is a string representing the locale code in the format of a language code
 *   and region code (e.g., "en-GB" for British English, "de-DE" for German - Germany).
 * - The value is the corresponding locale data object which contains localized
 *   content or configurations for the given locale.
 */
export const localeMap: any = {
    "en-GB": enLocale,
    "de-DE": deLocale,
};

/**
 * Key identifier of the language selected by the user.
 * The value in local storage will be the locale of the language
 */
export const languageLocalStorageKey = "language";


/**
 * Retrieves the language object based on the provided locale string.
 *
 * @param {string} locale - The locale identifier that specifies the desired language.
 * @return {Language} The corresponding language object for the given locale. Defaults to English if no match is found.
 */
export function getLanguageByLocale(locale: string): Language {

    switch( locale ) {
        case English.locale:
            return English;
        case German.locale:
            return German;
        default:
            return English;
    }

}

/**
 * Determines the appropriate language to use based on the browser's language settings.
 * The method checks the browser's preferred languages, matching them against predefined options.
 * If German ("de") is detected among the languages, it will return `German`.
 * If English ("en") is detected or no other match is found, it returns `English`.
 *
 * @return {Language} The language determined from the browser's language settings, either `German` or `English`.
 */
export function getLanguageByBrowserLanguage(): Language {


    const languages = navigator.languages
        .map( lang => lang.toLowerCase())

    for( let i = 0; i < languages.length ; i++ ) {
        if( languages[ i ].includes( "de" )  ) {
            return German;
        }
        if(  languages[ i ].includes("en") ) {
            return English;
        }
    }

    return English;
}


/**
 * LanguageDefinitionButtonAndTooltip interface combines the properties and functionalities
 * of both LanguageDefinitionButton and LanguageDefinitionTooltip interfaces.
 *
 * It serves as a unified definition for UI components that involve both button behavior
 * and tooltip support, which are commonly used in multilingual or internationalized applications.
 *
 * This interface extends:
 * - LanguageDefinitionButton: Defines the properties and behaviors specific to language-related buttons.
 * - LanguageDefinitionTooltip: Defines the properties and behaviors for tooltips, providing additional
 *   context or information for the buttons.
 */
export interface LanguageDefinitionButtonAndTooltip extends LanguageDefinitionButton, LanguageDefinitionTooltip {}

/**
 * Interface representing the structure of language-specific definitions and translations
 * used throughout the application. It defines various keys and properties that denote
 * translations, placeholders, and tooltips for UI elements, errors, notifications, and
 * more.
 */
export interface Language {
    locale: string
    languageIdentifier: string
    language: string
    cookieBanner: CookieBannerLangDef
    common: {
        description: string
        reason: string
        control: string
        contractor: string
        mainMenu: string
        invoices: string
        reports: string
        immediately: string
        replace: string
        origin: string
        emptyCache: LanguageDefinitionButtonAndTooltip
        sender: string,
        recipient: string,
        status:string,
        import: LanguageDefinitionButtonAndTooltip
        export: LanguageDefinitionButtonAndTooltip
        unsavedDataWarning: HelperTextLanguageDefinition
        total: string,
        files: string,
        uploadFile: LanguageDefinitionButtonAndTooltip
        remaining: string
        active: string
        not: string
        filterInvert: LanguageDefinitionButtonAndTooltip
        add: string
        internal: string
        newEntry: LanguageDefinitionButtonAndTooltip
        stepBack: LanguageDefinitionButtonAndTooltip
        stepForward: LanguageDefinitionButtonAndTooltip
        searchBox: LanguageDefinitionInputPlaceholder
        editEntry: LanguageDefinitionButtonAndTooltip
        deleteEntry: LanguageDefinitionButtonAndTooltip
        restoreEntry: LanguageDefinitionButtonAndTooltip
        currentEntry: string
        cancel: LanguageDefinitionButtonAndTooltip
        isDeleted: string
        showDetail: LanguageDefinitionButtonAndTooltip
        sorting: LanguageDefinitionButtonAndTooltip
        sortAsc: LanguageDefinitionButtonAndTooltip
        sortDesc: LanguageDefinitionButtonAndTooltip
        filterButton: LanguageDefinitionButtonAndTooltip
        backToHome: LanguageDefinitionTooltip
        markAsRead: LanguageDefinitionButtonAndTooltip
        markAsUnread: LanguageDefinitionButtonAndTooltip
        expandText: LanguageDefinitionButtonAndTooltip
        contractText: LanguageDefinitionButtonAndTooltip
        closeButton: LanguageDefinitionButtonAndTooltip
        jumpToEntry: LanguageDefinitionButtonAndTooltip
        submitButton: LanguageDefinitionButton
        resetButton: LanguageDefinitionButton
        importantTitle: string
        removeButton: LanguageDefinitionButtonAndTooltip
        inputSwitch: {
            labelTrue: string
            labelFalse: string
        }
        confirmDelete: {
            question: string
            standardText: string
        },
        confirmRestore: {
            question: string,
            standardText: string
        },
        tabBaiscInfo: string,
        all: string,
        justSelected: string
        onlyCurrent: string
        per: string
        yes: string
        no: string
        address: string,
        disablePlaceSearch: HelperTextLanguageDefinition
        street: string,
        houseNumber: string,
        city: string,
        country: string,
        province: string,
        postal: string
        contactPlural: string
        contactSingular: string
        name: string,
        office: string,
        allowed: string,
        module: string,
        begin: string,
        end: string,
        userDefined: string,
        notAssigned: string,
        selectPlaceholder: string,
        lockStatusTitle: LanguageDefinitionButtonAndTooltip,
        lockStatus: {
            0: {
                title: string,
                tooltip: string
            },
            1: {
                title: string,
                tooltip: string
            },
            2: {
                title: string,
                tooltip: string
            },
            3: {
                title: string,
                tooltip: string
            }
        },
        form: {
            lockedValues: HelperTextLanguageDefinition,
            removeLockValues: string
        }
        tag: LanguageDefinitionButtonAndTooltip,
        category: LanguageDefinitionButtonAndTooltip,
        dateName: string,
        entry: string,
        attachment: LanguageDefinitionButtonAndTooltip,
        summary: string
        solvencyReport: string
    }
    customers: CustomerLanguageDefinition
    ajaxResponse: LanguageDefinitionAjaxResponses
    groups: LanguageDefinitionUserGroups
    timesheet: LanguageDefinitionTimesheet
    timesheet_tasks: LanguageDefinitionTimesheetTask
    help: LanguageDefinitionHelp
    notification: LanguageDefinitionNotifications
    userSettings: LanguageDefinitionUserSettings
    projects: LanguageDefinitionProject
    errors: LanguageDefinitionError
    privileges: LanguageDefinitionPrivileges
    userManagment: LanguageDefinitionUserManagement
    employees: LanguageDefinitionEmployeeView
    employeeDetailView: LanguageDefinitionEmployeeDetailView
    employeeContract: LanguageDefinitionEmployeeContract,
    employers: LanguageDefinitionEmployer,
    calendar: LanguageDefinitionCalendar,
    communication: {
        coms: LanguageDefinitionCommunication,
        methods: LanguageDefinitionCommunicationMethod
    }
    qualifications: LanguageDefinitionQualification
    projectLog: LanguageDefinitionProjectLog
    contractors: LanguageDefinitionContractor
    externalWork: ExternalWorkLanguageDefinition
    externalWorkGroup: ExternalWorkGroupLanguageDefinition
    externalWorkArea: ExternalWorkAreaLanguageDefinition
    projectCommissionState: ProjectCommissionStateLanguageDef
    projectSegmentFilter: ProjectSegmentFilterLanguageDef
    projectRoles: ProjectRolesLanguageDef
    accounting: AccountingLanguageDef
    usercp: LanguageDefinitionUserCP
    team: TeamLanguageDef
    mainMenu: {
        outgoing_invoice: { title: string }
        incoming_invoice: { title: string }
        solvency_projection: { title: string }
        project_report: { title: string }
        project_roles: { title: string }
        commission_state: { title: string }
        reporting_employees_hours: { title: string }
        external_works: { title: string }
        external_work_groups: { title: string }

    },
    api_keys: ApiKeysLangDef

}

